/**
 * Teams Balance Board — bright green tabular scoreboard suitable for LED / OBS.
 * Rows sortable by remaining budget. Toggle "Can Bid" column. Per-team buttons
 * open a squad list with either photos or price-tags below the table.
 */
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { resolveImageUrl } from "@/components/ImageInput";
import { PHOTO_FALLBACK, usePublicAuction, brandLogoFor } from "./_shared";

export function OverlayTeamsBalance() {
  const { auctionId } = useParams();
  const { auction, teams, players } = usePublicAuction(auctionId);
  const [focusTeamId, setFocusTeamId] = useState(null);
  const [showImages, setShowImages] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const [canBidToggle, setCanBidToggle] = useState(false);

  const settings = auction?.settings || {};
  const basePrice = settings.default_base_price || 10;
  const minPlayers = settings.min_players_per_team || 11;

  const rows = useMemo(() => {
    return teams
      .slice()
      .sort((a, b) => (b.remaining_budget || 0) - (a.remaining_budget || 0))
      .map((t) => {
        const bought = players.filter((p) => p.sold_to === t.id);
        const slotsAfter = Math.max(0, minPlayers - bought.length - 1);
        const maxBid = Math.max(0, (t.remaining_budget || 0) - slotsAfter * basePrice);
        return { ...t, bought, players_bought: bought.length, max_bid: maxBid };
      });
  }, [teams, players, basePrice, minPlayers]);

  const focusTeam = focusTeamId ? rows.find((r) => r.id === focusTeamId) : null;

  const toggleTeamView = (teamId, mode) => {
    if (focusTeamId === teamId && ((mode === "images" && showImages) || (mode === "price" && showPrice))) {
      setFocusTeamId(null);
      setShowImages(false);
      setShowPrice(false);
      return;
    }
    setFocusTeamId(teamId);
    setShowImages(mode === "images");
    setShowPrice(mode === "price");
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-8" data-testid="overlay-teams-balance">
      <div className="max-w-7xl mx-auto rounded-lg overflow-hidden border border-emerald-700 shadow-2xl">
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <img src={brandLogoFor(auction)} alt="" className="h-8" />
            <div>
              <div className="text-xs uppercase tracking-widest opacity-90">Teams Balance</div>
              <div className="font-display text-2xl leading-none uppercase">{auction?.name || "Auction"}</div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="teams-balance-can-bid-toggle">
            <input
              type="checkbox"
              checked={canBidToggle}
              onChange={(e) => setCanBidToggle(e.target.checked)}
              className="w-4 h-4 accent-white"
            />
            <span className="uppercase tracking-widest text-xs font-bold">Can Bid</span>
          </label>
        </div>
        <div className="overflow-x-auto bg-emerald-50">
          <table className="w-full text-sm" data-testid="teams-balance-table">
            <thead className="bg-zinc-500 text-white">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Sr</th>
                <th className="px-3 py-2 text-left font-semibold">Team</th>
                <th className="px-3 py-2 text-right font-semibold">Purse</th>
                <th className="px-3 py-2 text-right font-semibold">Balance</th>
                <th className="px-3 py-2 text-right font-semibold">Max Bid</th>
                {canBidToggle && <th className="px-3 py-2 text-center font-semibold">Can Bid</th>}
                <th className="px-3 py-2 text-center font-semibold">Players</th>
                <th className="px-3 py-2 text-center font-semibold">Squad</th>
                <th className="px-3 py-2 text-center font-semibold">Squad</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => {
                const atMax = t.players_bought >= (t.max_players || 15);
                const isFocused = focusTeamId === t.id;
                return (
                  <tr key={t.id} className={`border-b border-emerald-300/70 hover:bg-emerald-200/60 ${isFocused ? "bg-emerald-300/60" : "bg-emerald-100/80"}`} data-testid={`teams-balance-row-${t.id}`}>
                    <td className="px-3 py-2 text-zinc-800 font-mono">{i + 1}</td>
                    <td className="px-3 py-2 text-zinc-900 font-semibold">
                      <div className="flex items-center gap-2">
                        {t.logo_url && (
                          <img src={resolveImageUrl(t.logo_url)} alt="" className="w-6 h-6 rounded object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                        )}
                        {t.name}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-800">{(t.budget || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-800">{(t.remaining_budget || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-800">{atMax ? "-" : t.max_bid.toLocaleString()}</td>
                    {canBidToggle && (
                      <td className="px-3 py-2 text-center font-mono text-zinc-800">
                        {atMax ? "No" : "Yes"}
                      </td>
                    )}
                    <td className="px-3 py-2 text-center text-zinc-800 font-mono">{t.players_bought}/{t.max_players || 15}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleTeamView(t.id, "images")}
                        className={`inline-block text-white text-xs font-bold uppercase px-3 py-1 rounded-full transition-colors ${isFocused && showImages ? "bg-emerald-700" : "bg-emerald-500 hover:bg-emerald-600"}`}
                        data-testid={`teams-balance-squad-images-${t.id}`}
                      >
                        with images
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleTeamView(t.id, "price")}
                        className={`inline-block text-white text-xs font-bold uppercase px-3 py-1 rounded-full transition-colors ${isFocused && showPrice ? "bg-emerald-700" : "bg-emerald-500 hover:bg-emerald-600"}`}
                        data-testid={`teams-balance-squad-price-${t.id}`}
                      >
                        with price
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-zinc-500">No teams yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {focusTeam && (showImages || showPrice) && (
          <div className="bg-white p-4 border-t border-emerald-300" data-testid="teams-balance-squads">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2" data-testid={`teams-balance-squad-of-${focusTeam.id}`}>
                {focusTeam.logo_url && (
                  <img src={resolveImageUrl(focusTeam.logo_url)} alt="" className="w-8 h-8 rounded object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                )}
                <div className="font-display text-xl uppercase text-zinc-900">{focusTeam.name}</div>
                <div className="text-xs text-zinc-500">({focusTeam.bought.length} players)</div>
              </div>
              <button
                type="button"
                onClick={() => { setFocusTeamId(null); setShowImages(false); setShowPrice(false); }}
                className="text-xs uppercase font-bold text-zinc-600 hover:text-zinc-900"
                data-testid="teams-balance-close"
              >
                ✕ Close
              </button>
            </div>
            {focusTeam.bought.length === 0 ? (
              <div className="text-sm text-zinc-500 italic">No players purchased yet.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {focusTeam.bought.map((p) => (
                  <div key={p.id} className="border border-emerald-200 rounded overflow-hidden bg-emerald-50/60 flex flex-col" data-testid={`teams-balance-focus-player-${p.id}`}>
                    {showImages && (
                      <img
                        src={p.photo_url ? resolveImageUrl(p.photo_url) : PHOTO_FALLBACK}
                        alt={p.name}
                        className="w-full aspect-[3/4] object-cover"
                        onError={(e) => { e.target.src = PHOTO_FALLBACK; }}
                      />
                    )}
                    <div className="px-2 py-1 text-[11px] text-zinc-800">
                      <div className="font-semibold truncate">{p.name}</div>
                      {p.category === "Icon" && <div className="text-amber-600 text-[9px] font-bold uppercase">Icon</div>}
                      {showPrice && (
                        <div className="text-emerald-700 font-mono">{p.sold_price} pts</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
